import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import TicketReceipt from '../components/TicketReceipt';
import { Printer, ArrowLeft, Info, SpinnerGap } from '@phosphor-icons/react';
import { printReceiptInline } from '../lib/receiptHtml';
import { listPaymentTerminals } from '../services/paymentTerminalsApi';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const TicketPage = () => {
    const { saleId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [sale, setSale] = useState(null);
    const [company, setCompany] = useState({});
    const [receiptConfig, setReceiptConfig] = useState(null);
    const [tpvMap, setTpvMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [printing, setPrinting] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [s, c, rc, tpvs] = await Promise.all([
                    axios.get(`${API}/sales/${saleId}`, { headers: authHeaders() }),
                    axios.get(`${API}/company`, { headers: authHeaders() }),
                    axios.get(`${API}/company/receipt-config`, { headers: authHeaders() }),
                    listPaymentTerminals().catch(() => []),
                ]);
                setSale(s.data);
                setCompany(c.data || {});
                setReceiptConfig(rc.data || {});
                const map = {};
                (tpvs || []).forEach(t => { map[t.id] = t.name; });
                setTpvMap(map);
            } catch (err) {
                setError('No se pudo cargar la venta');
            } finally {
                setLoading(false);
            }
        })();
    }, [saleId]);

    /**
     * Direct in-app printing — no PDF, no download, no new tab.
     * Renders the receipt into a hidden iframe and triggers
     * iframe.contentWindow.print(), which delegates to the device's
     * native print system: Fully Kiosk / Android print framework
     * (Mopria / Bluetooth), AirPrint on iPad, system printers on desktop.
     */
    const handlePrint = async () => {
        if (!sale || !receiptConfig) return;
        setPrinting(true);
        try {
            await printReceiptInline({
                sale,
                company,
                config: receiptConfig,
                appUrl: window.location.origin,
                tpvMap,
            });
        } catch (err) {
            toast.error('No se pudo abrir el diálogo de impresión');
        } finally {
            // Re-enable the button shortly after — the iframe print() call is
            // synchronous; the dialog itself runs in a separate browser layer.
            setTimeout(() => setPrinting(false), 800);
        }
    };

    if (loading) return <div className="p-8 text-center text-[#8B8474]">Cargando...</div>;
    if (error) return <div className="p-8 text-center text-red-600">{error}</div>;

    return (
        <div className="min-h-screen flex flex-col items-center justify-start py-8 px-4 bg-[#F4ECE0]">
            <div className="w-full max-w-md no-print">
                <div className="flex items-center justify-between mb-4 gap-2">
                    <Button
                        variant="outline"
                        onClick={() => navigate(-1)}
                        data-testid="ticket-back-btn"
                        className="h-10 rounded-md border-[#ECE3D2] text-[#1F2D4B] hover:bg-white"
                    >
                        <ArrowLeft size={14} className="mr-1.5" /> Volver
                    </Button>
                    <div className="flex gap-2">
                        <Button
                            onClick={handlePrint}
                            disabled={printing}
                            data-testid="ticket-print-btn"
                            className="h-10 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md disabled:opacity-60"
                        >
                            {printing
                                ? <SpinnerGap size={14} className="animate-spin mr-1.5" />
                                : <Printer size={14} weight="bold" className="mr-1.5" />}
                            Imprimir
                        </Button>
                    </div>
                </div>

                <div className="bg-[#FFF8E7] border border-[#F5DEB3] rounded-md p-3 text-[12px] text-[#7A4F00] mb-4 flex gap-2">
                    <Info size={16} weight="duotone" className="flex-shrink-0 mt-0.5" />
                    <div>
                        Pulsa <b>Imprimir</b> — se abre directamente el diálogo de impresión del
                        dispositivo (sin PDF, sin nueva pestaña). Compatible con
                        Fully Kiosk, AirPrint, Mopria y la mayoría de impresoras térmicas WiFi/Bluetooth.
                    </div>
                </div>

                <div className="bg-white rounded-md shadow-lg overflow-hidden" data-testid="ticket-preview">
                    <TicketReceipt sale={sale} company={company} appUrl={window.location.origin} />
                </div>
            </div>
        </div>
    );
};

export default TicketPage;
